import React, { useState } from 'react';
import { useCollegePrepContext } from '../../contexts/CollegePrepContext';

const ResearchTools = () => {
  const { researchTasks, addResearchTask, updateResearchTask, deleteResearchTask } = useCollegePrepContext();
  const [activeTab, setActiveTab] = useState('tools');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    programId: '',
    type: 'research',
    priority: 'medium'
  });

  const researchTools = [
    {
      id: 'quick-add',
      title: 'Quick Add Program',
      description: 'Found a program? Add it to your collection quickly',
      icon: '➕',
      action: 'Add Program'
    },
    {
      id: 'deep-research',
      title: 'Deep Research',
      description: 'Comprehensive research template for programs',
      icon: '🔍',
      action: 'Start Research'
    },
    {
      id: 'compare',
      title: 'Compare Programs',
      description: 'Side-by-side comparison of your programs',
      icon: '📊',
      action: 'Compare'
    },
    {
      id: 'strategy',
      title: 'Research Strategy',
      description: 'Guide for effective program research',
      icon: '💡',
      action: 'Learn More'
    }
  ];

  const researchSources = [
    {
      category: 'Official Sources',
      sources: [
        'College/university summer program pages',
        'Program-specific websites and applications',
        'University admissions offices'
      ]
    },
    {
      category: 'Search Databases',
      sources: [
        'Fastweb summer program database',
        "Peterson's summer programs",
        'College Board summer program search',
        'BigFuture by College Board'
      ]
    },
    {
      category: 'Community Research',
      sources: [
        'Reddit communities (r/ApplyingToCollege)',
        'College Confidential forums',
        'Discord servers for specific programs'
      ]
    },
    {
      category: 'Networking',
      sources: [
        'LinkedIn to find program alumni',
        'Local organizations and nonprofits',
        'School counselors and teachers',
        'Alumni from your school'
      ]
    }
  ];

  const handleAddTask = (e) => {
    e.preventDefault();
    if (newTask.title) {
      addResearchTask(newTask);
      setNewTask({
        title: '',
        description: '',
        programId: '',
        type: 'research',
        priority: 'medium'
      });
      setShowTaskForm(false);
    }
  };

  const toggleTaskComplete = (taskId) => {
    const task = researchTasks.find(t => t.id === taskId);
    updateResearchTask(taskId, { completed: !task.completed });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'text-red-600 bg-red-50',
      'medium': 'text-amber-600 bg-amber-50',
      'low': 'text-emerald-600 bg-emerald-50'
    };
    return colors[priority] || 'text-stone-600 bg-stone-50';
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Research Tools</h2>
          <p className="text-stone-600 mt-1">
            Templates and guides for deep program research
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-stone-200 mb-6">
        <button
          onClick={() => setActiveTab('tools')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'tools'
              ? 'border-emerald-500 text-emerald-600'
              : 'border-transparent text-stone-600 hover:text-stone-900'
          }`}
        >
          Research Tools
        </button>
        <button
          onClick={() => setActiveTab('sources')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'sources'
              ? 'border-emerald-500 text-emerald-600'
              : 'border-transparent text-stone-600 hover:text-stone-900'
          }`}
        >
          Research Sources
        </button>
        <button
          onClick={() => setActiveTab('tasks')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'tasks'
              ? 'border-emerald-500 text-emerald-600'
              : 'border-transparent text-stone-600 hover:text-stone-900'
          }`}
        >
          Research Tasks ({researchTasks.length})
        </button>
      </div>

      {/* Tools Tab */}
      {activeTab === 'tools' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {researchTools.map((tool) => (
            <div key={tool.id} className="bg-white border border-stone-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
              <div className="text-center">
                <div className="text-3xl mb-3">{tool.icon}</div>
                <h3 className="font-semibold text-slate-900 mb-2">{tool.title}</h3>
                <p className="text-sm text-stone-600 mb-4">{tool.description}</p>
                <button className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700">
                  {tool.action}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sources Tab */}
      {activeTab === 'sources' && (
        <div className="space-y-6">
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
            <h3 className="font-semibold text-sky-800 mb-2">Research Best Practices</h3>
            <ul className="text-sm text-sky-700 space-y-1">
              <li>• Start with official program websites for accurate information</li>
              <li>• Cross-reference information across multiple sources</li>
              <li>• Take detailed notes and save URLs for later reference</li>
              <li>• Look for application requirements, deadlines, and selection criteria</li>
              <li>• Research alumni outcomes and program reputation</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {researchSources.map((category) => (
              <div key={category.category} className="bg-white border border-stone-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-3">{category.category}</h3>
                <ul className="space-y-2">
                  {category.sources.map((source, index) => (
                    <li key={index} className="text-sm text-stone-600 flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>{source}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-slate-900">Research Tasks</h3>
              <p className="text-sm text-stone-600">Keep track of your research progress</p>
            </div>
            <button
              onClick={() => setShowTaskForm(true)}
              className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700"
            >
              Add Task
            </button>
          </div>

          {/* Add Task Form */}
          {showTaskForm && (
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-3">Add Research Task</h4>
              <form onSubmit={handleAddTask} className="space-y-3">
                <div>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Task title"
                    required
                  />
                </div>
                <div>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Task description"
                    rows="2"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                    className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTaskForm(false)}
                    className="px-4 py-2 border border-stone-300 text-stone-700 font-medium rounded-lg hover:bg-stone-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tasks List */}
          {researchTasks.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">📝</div>
              <h4 className="font-semibold text-slate-900 mb-2">No research tasks yet</h4>
              <p className="text-stone-600">Add tasks to keep track of your program research.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {researchTasks.map((task) => (
                <div key={task.id} className={`border rounded-lg p-4 ${task.completed ? 'bg-stone-50 border-stone-200' : 'bg-white border-stone-200'}`}>
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleTaskComplete(task.id)}
                      className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center ${
                        task.completed
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-stone-300 hover:border-emerald-300'
                      }`}
                    >
                      {task.completed && '✓'}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`font-medium ${task.completed ? 'text-stone-500 line-through' : 'text-slate-900'}`}>
                          {task.title}
                        </h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                      {task.description && (
                        <p className={`text-sm ${task.completed ? 'text-stone-400' : 'text-stone-600'}`}>
                          {task.description}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteResearchTask(task.id)}
                      className="text-stone-400 hover:text-red-500 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResearchTools;